import React, { useState, useEffect } from 'react';
import { MapPin, Search, Eye, Info, Package, Grid3X3, Layers, Box, Navigation, ZoomIn, ZoomOut } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseMap = ({
  selectedLocation,
  onLocationSelect,
  showOccupancy = true,
  racks: propsRacks,
  occupancyData: propsOccupancyData
}) => {
  const [racks, setRacks] = useState([]);
  const [occupancyData, setOccupancyData] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('overview'); // 'overview' | 'detailed'
  const [selectedRack, setSelectedRack] = useState(null);
  const [rackItems, setRackItems] = useState([]); // Items on selected rack
  const [loadingItems, setLoadingItems] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Use props data if available, otherwise fetch warehouse data
  useEffect(() => {
    if (propsRacks && propsRacks.length > 0 && propsOccupancyData) {
      console.log('Using props data - racks:', propsRacks.length, 'occupancy:', Object.keys(propsOccupancyData).length);
      setRacks(propsRacks);
      setOccupancyData(propsOccupancyData);
      setLoading(false);
    } else {
      fetchWarehouseData();
    }
  }, [propsRacks, propsOccupancyData]);

  const fetchWarehouseData = async () => {
    try {
      setLoading(true);

      // Fetch racks structure
      const racksResponse = await axios.get(`${API_BASE_URL}/api/warehouse/structure/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const racksData = Array.isArray(racksResponse.data) ? racksResponse.data :
                       racksResponse.data.results ? racksResponse.data.results : [];
      setRacks(racksData);

      // Fetch occupancy data if needed
      if (showOccupancy) {
        try {
          // Try to fetch all items first to get complete occupancy data (including multi-location items)
          const itemsResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          console.log('WarehouseMap - All items response:', itemsResponse.data);

          const items = itemsResponse.data.results || itemsResponse.data || [];
          const occupancyMap = {};

          items.forEach(item => {
            // Handle single location
            if (item.location) {
              // Handle locations stored as comma-separated string (multi-location items)
              if (item.location.includes(',')) {
                const locations = item.location.split(',').map(loc => loc.trim());
                locations.forEach(location => {
                  if (location) {
                    occupancyMap[location] = true;
                  }
                });
              } else {
                // Single location
                occupancyMap[item.location] = true;
              }
            }

            // Handle multiple locations array (if item.locations exists)
            if (item.locations && Array.isArray(item.locations)) {
              item.locations.forEach(location => {
                if (location) {
                  occupancyMap[location] = true;
                }
              });
            }
          });

          console.log('WarehouseMap - Complete occupancy map from items:', occupancyMap);
          setOccupancyData(occupancyMap);
        } catch (itemsError) {
          console.error('WarehouseMap - Error fetching items, falling back to occupancy endpoint:', itemsError);

          // Fallback to occupancy endpoint
          const occupancyResponse = await axios.get(`${API_BASE_URL}/api/warehouse/structure/occupancy/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          const occupancyDataArray = Array.isArray(occupancyResponse.data) ? occupancyResponse.data :
                                    occupancyResponse.data.results ? occupancyResponse.data.results : [];
          const occupancyMap = {};
          occupancyDataArray.forEach(item => {
            occupancyMap[item.location] = true;
          });
          setOccupancyData(occupancyMap);
        }
      }
    } catch (error) {
      console.error('Error fetching warehouse data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate location code
  const generateLocationCode = (rackName, shelf, container) => {
    const shelfStr = shelf.toString().padStart(2, '0');
    const containerStr = String.fromCharCode(65 + container);
    return `${rackName}-${shelfStr}-${containerStr}`;
  };

  // Get all locations for a rack
  const getRackLocations = (rack) => {
    const locations = [];
    for (let shelf = 1; shelf <= rack.shelves; shelf++) {
      for (let container = 0; container < rack.containers; container++) {
        const code = generateLocationCode(rack.name, shelf, container);
        locations.push({
          code,
          shelf,
          container,
          occupied: occupancyData[code] || false
        });
      }
    }
    return locations;
  };

  // Fetch items for a specific rack
  const fetchRackItems = async (rack) => {
    setLoadingItems(true);
    try {
      const locations = getRackLocations(rack);
      const locationCodes = locations.map(loc => loc.code);

      // Fetch warehouse items
      const warehouseResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      // Fetch equipment items
      const equipmentResponse = await axios.get(`${API_BASE_URL}/api/safetyequipment/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const warehouseItems = warehouseResponse.data.results || warehouseResponse.data || [];
      const equipmentItems = equipmentResponse.data.results || equipmentResponse.data || [];

      const itemsOnRack = [];

      // Process warehouse items
      warehouseItems.forEach(item => {
        if (item.location) {
          const itemLocations = item.location.includes(',')
            ? item.location.split(',').map(l => l.trim())
            : [item.location];

          itemLocations.forEach(loc => {
            if (locationCodes.includes(loc)) {
              // Build picture URL
              let pictureUrl = null;
              if (item.item_picture) {
                pictureUrl = item.item_picture.startsWith('http')
                  ? item.item_picture
                  : `${API_BASE_URL}${item.item_picture.startsWith('/') ? '' : '/'}${item.item_picture}`;
              }

              itemsOnRack.push({
                name: item.name,
                location: loc,
                quantity: item.quantity_in_stock || 0,
                unit: item.unit || 'pcs',
                type: 'warehouse',
                category: item.category_name || 'Unknown',
                picture: pictureUrl
              });
            }
          });
        }
      });

      // Process equipment items
      equipmentItems.forEach(item => {
        const location = item.safety_equipment_location;
        if (location && location !== 'Warehouse') {
          const itemLocations = location.includes(',')
            ? location.split(',').map(l => l.trim())
            : [location];

          itemLocations.forEach(loc => {
            if (locationCodes.includes(loc)) {
              // Build picture URL - prefer item picture, fallback to model image
              let pictureUrl = null;
              const pictureSource = item.safety_equipment_picture || item.safety_equipment_model_image;
              if (pictureSource) {
                pictureUrl = pictureSource.startsWith('http')
                  ? pictureSource
                  : `${API_BASE_URL}${pictureSource.startsWith('/') ? '' : '/'}${pictureSource}`;
              }

              // Check if already exists (for equipment, count each item)
              const existing = itemsOnRack.find(i =>
                i.name === item.safety_equipment_type_name &&
                i.location === loc &&
                i.type === 'equipment'
              );

              if (existing) {
                existing.quantity += 1;
                // Update picture if this item has one and existing doesn't
                if (pictureUrl && !existing.picture) {
                  existing.picture = pictureUrl;
                }
              } else {
                itemsOnRack.push({
                  name: item.safety_equipment_type_name,
                  location: loc,
                  quantity: 1,
                  unit: 'pcs',
                  type: 'equipment',
                  category: 'Equipment',
                  serialNumber: item.safety_equipment_serial_number,
                  picture: pictureUrl
                });
              }
            }
          });
        }
      });

      // Sort by location
      itemsOnRack.sort((a, b) => a.location.localeCompare(b.location));

      setRackItems(itemsOnRack);
    } catch (error) {
      console.error('Error fetching rack items:', error);
      setRackItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  // Handle rack click
  const handleRackClick = (rack) => {
    if (selectedRack?.id === rack.id) {
      // Deselect if clicking the same rack
      setSelectedRack(null);
      setRackItems([]);
    } else {
      setSelectedRack(rack);
      fetchRackItems(rack);
    }
  };

  // Filter racks based on search
  const filteredRacks = racks.filter(rack => 
    rack.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    searchTerm === '' ||
    getRackLocations(rack).some(loc => 
      loc.code.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Handle location selection
  const handleLocationSelect = (locationCode) => {
    if (onLocationSelect) {
      onLocationSelect(locationCode);
    }
  };

  // Rack component for overview mode - improved visualization
  const RackOverview = ({ rack, style }) => {
    const locations = getRackLocations(rack);
    const occupiedCount = locations.filter(loc => loc.occupied).length;
    const occupancyPercentage = (occupiedCount / locations.length) * 100;

    return (
      <div
        className={`bg-gradient-to-br from-white to-gray-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-200 hover:shadow-xl hover:scale-105 ${
          selectedRack?.id === rack.id ? 'border-blue-500 shadow-lg ring-2 ring-blue-200' : 'border-gray-300 hover:border-blue-300'
        }`}
        style={style}
        onClick={() => handleRackClick(rack)}
        title={`Rack ${rack.name} - ${occupiedCount}/${locations.length} locations occupied - Click to view items`}
      >
        {/* Rack Header */}
        <div className="text-center mb-3">
          <div className="text-xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {rack.name}
          </div>
          <div className="text-xs text-gray-500 font-medium mt-1">
            {rack.shelves} shelves × {rack.containers} containers
          </div>
        </div>

        {/* Enhanced rack visualization with 3D effect */}
        <div className="space-y-1 bg-gray-100 p-2 rounded-lg border border-gray-200">
          {Array.from({ length: Math.min(rack.shelves, 8) }, (_, shelfIndex) => (
            <div key={shelfIndex} className="flex space-x-1">
              {Array.from({ length: rack.containers }, (_, containerIndex) => {
                const locationCode = generateLocationCode(rack.name, shelfIndex + 1, containerIndex);
                const isOccupied = occupancyData[locationCode];
                const isSelected = selectedLocation === locationCode;

                return (
                  <div
                    key={containerIndex}
                    className={`w-3 h-3 rounded transition-all duration-200 border ${
                      isSelected
                        ? 'bg-blue-500 border-blue-700 shadow-md scale-110'
                        : isOccupied
                        ? 'bg-red-400 border-red-600 shadow-sm'
                        : 'bg-green-400 border-green-600 hover:bg-green-500'
                    }`}
                    style={{
                      boxShadow: isSelected ? '0 0 8px rgba(59, 130, 246, 0.6)' : undefined
                    }}
                  />
                );
              })}
            </div>
          ))}
          {rack.shelves > 8 && (
            <div className="text-xs text-gray-400 text-center font-semibold">+{rack.shelves - 8} more</div>
          )}
        </div>

        {/* Enhanced occupancy bar with gradient */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600 font-medium">Fullness</span>
            <span className="text-xs font-bold text-gray-900">{Math.round(occupancyPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${
                occupancyPercentage > 80 ? 'bg-gradient-to-r from-red-400 to-red-600' :
                occupancyPercentage > 50 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                'bg-gradient-to-r from-green-400 to-green-600'
              }`}
              style={{ width: `${occupancyPercentage}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 text-center mt-1">
            {occupiedCount} / {locations.length} locations
          </div>
        </div>
      </div>
    );
  };

  // Detailed rack view - improved
  const RackDetailed = ({ rack }) => {
    const locations = getRackLocations(rack);
    const occupiedCount = locations.filter(loc => loc.occupied).length;

    return (
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl border-2 border-blue-200 p-6 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              Rack <span className="text-blue-600">{rack.name}</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {rack.shelves} shelves × {rack.containers} containers | {occupiedCount}/{locations.length} occupied
            </p>
          </div>
          <button
            onClick={() => {setViewMode('overview'); setSelectedRack(null);}}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-md"
          >
            ← Back to Overview
          </button>
        </div>

        <div className="space-y-3 bg-white p-4 rounded-lg border border-gray-200">
          {Array.from({ length: rack.shelves }, (_, shelfIndex) => {
            const shelfNum = shelfIndex + 1;
            return (
              <div key={shelfIndex} className="flex items-center space-x-3">
                {/* Shelf label */}
                <div className="w-14 text-right flex-shrink-0">
                  <span className="text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-2 rounded-lg shadow-sm">
                    {shelfNum.toString().padStart(2, '0')}
                  </span>
                </div>

                {/* Containers */}
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: rack.containers }, (_, containerIndex) => {
                    const locationCode = generateLocationCode(rack.name, shelfNum, containerIndex);
                    const isOccupied = occupancyData[locationCode];
                    const isSelected = selectedLocation === locationCode;
                    const isHovered = hoveredLocation === locationCode;

                    return (
                      <div key={containerIndex} className="relative">
                        <button
                          onClick={() => handleLocationSelect(locationCode)}
                          onMouseEnter={() => setHoveredLocation(locationCode)}
                          onMouseLeave={() => setHoveredLocation(null)}
                          className={`w-12 h-10 text-sm font-mono font-bold rounded-lg border-2 transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-blue-500 text-white border-blue-600 shadow-lg transform scale-110 ring-2 ring-blue-300'
                              : isHovered
                              ? 'bg-blue-100 border-blue-400 transform scale-110 shadow-lg ring-2 ring-blue-200'
                              : isOccupied
                              ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100 hover:shadow-md'
                              : 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100 hover:border-green-400 hover:shadow-md transform hover:scale-105'
                          }`}
                          title={`${locationCode} - ${isOccupied ? 'Occupied' : 'Available'}`}
                        >
                          {String.fromCharCode(65 + containerIndex)}
                        </button>
                        {/* Status indicator */}
                        {isOccupied && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full shadow-md border-2 border-white"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full h-96 bg-gray-50 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading warehouse map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Navigation className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Warehouse Map</h2>
              <p className="text-sm text-gray-600">
                {viewMode === 'overview' ? 'Click a rack to see detailed view' : 'Detailed rack view'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Search */}
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search racks or locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* View toggle */}
            <button
              onClick={() => setViewMode(viewMode === 'overview' ? 'detailed' : 'overview')}
              className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              disabled={viewMode === 'detailed' && !selectedRack}
            >
              {viewMode === 'overview' ? <Eye className="h-4 w-4 mr-1" /> : <Grid3X3 className="h-4 w-4 mr-1" />}
              {viewMode === 'overview' ? 'Detailed' : 'Overview'}
            </button>
          </div>
        </div>
      </div>

      {/* Map Content */}
      <div className="p-4">
        {selectedRack ? (
          /* Split view when rack is selected */
          <div className="flex gap-4 h-[600px]">
            {/* Left side - Selected Rack */}
            <div className="w-5/12 flex flex-col">
              <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-blue-500 rounded-xl p-4 shadow-lg h-full overflow-y-auto">
                <RackOverview
                  rack={selectedRack}
                  style={{
                    minHeight: 'auto',
                    width: '100%'
                  }}
                />
              </div>
            </div>

            {/* Right side - Items Table */}
            <div className="w-7/12 bg-white rounded-lg border border-gray-300 p-4 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Package className="h-5 w-5 mr-2 text-blue-600" />
                  Items on Rack {selectedRack.name}
                </h3>
                <button
                  onClick={() => handleRackClick(selectedRack)}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold"
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {loadingItems ? (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Loading items...</p>
                  </div>
                </div>
              ) : rackItems.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">No items on this rack</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rackItems.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            {item.picture ? (
                              <img
                                src={item.picture}
                                alt={item.name}
                                className="w-12 h-12 object-cover rounded border border-gray-200"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                <span className="text-gray-400 text-xl">📦</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-mono font-bold text-gray-900">
                            {item.location}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="font-medium">{item.name}</div>
                            {item.serialNumber && (
                              <div className="text-xs text-gray-500 mt-0.5">S/N: {item.serialNumber}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              item.type === 'equipment'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {item.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {item.quantity} <span className="text-gray-500 font-normal">{item.unit}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary footer */}
              {!loadingItems && rackItems.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total items:</span>
                    <span className="font-semibold text-gray-900">{rackItems.length}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Grid view when no rack is selected */
          viewMode === 'overview' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {filteredRacks.map((rack, index) => (
                <RackOverview
                  key={rack.id}
                  rack={rack}
                  style={{
                    minHeight: '120px',
                    minWidth: '100px'
                  }}
                />
              ))}
            </div>
          ) : selectedRack ? (
            <RackDetailed rack={selectedRack} />
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Grid3X3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Select a rack from overview to see detailed view</p>
            </div>
          )
        )}
      </div>

      {/* Bottom info bar */}
      <div className="p-3 bg-white border-t border-gray-200 rounded-b-lg">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-400 rounded mr-2"></div>
              <span className="text-gray-600">Available</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-400 rounded mr-2"></div>
              <span className="text-gray-600">Occupied</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
              <span className="text-gray-600">Selected</span>
            </div>
          </div>

          <div className="text-gray-600">
            {racks.length} racks • {racks.reduce((sum, rack) => sum + rack.shelves * rack.containers, 0)} total locations
          </div>
        </div>
      </div>

      {/* Selected location info */}
      {selectedLocation && (
        <div className="p-3 bg-blue-50 border-t border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-blue-700">
              <MapPin className="h-4 w-4 mr-2" />
              <div>
                <div className="font-mono font-bold">{selectedLocation}</div>
                {selectedLocation.includes('-') && (
                  <div className="text-xs text-blue-600">
                    Rack: {selectedLocation.split('-')[0]} | 
                    Shelf: {selectedLocation.split('-')[1]} | 
                    Container: {selectedLocation.split('-')[2]}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseMap;